import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { FAQPageLd } from '../seo/jsonld/FAQPage';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQProps {
  /** Eyebrow optionnelle (chip avant le titre) */
  eyebrow?: string;
  title?: string;
  /** Description optionnelle sous le titre */
  description?: string;
  items: FAQItem[];
}

/**
 * Composant FAQ "block" utilisé par le CMS Puck.
 * Visuellement modernisé : eyebrow chip, layout centré, accordion premium.
 * Pour les FAQ d'audience (split layout), préférer `FaqSplit` ou `ParticulierFAQ`.
 */
export function FAQ({ eyebrow, title, description, items }: FAQProps) {
  return (
    <section className="relative overflow-hidden bg-paper py-20 md:py-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-spot-brand opacity-50"
      />
      <Container className="relative max-w-3xl">
        <Reveal>
          <div className="mb-10 text-center md:mb-14">
            {eyebrow && (
              <p className="eyebrow justify-center">{eyebrow}</p>
            )}
            {title && (
              <h2 className="mt-3 h-section">{title}</h2>
            )}
            {description && (
              <p className="mt-3 text-[15px] leading-relaxed text-stone-600">
                {description}
              </p>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <Accordion
            type="single"
            collapsible
            className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-card"
          >
            {items.map((item, idx) => (
              <AccordionItem
                key={item.question}
                value={`q-${idx}`}
                className="border-b border-stone-100 px-6 last:border-b-0"
              >
                <AccordionTrigger className="py-5 text-left text-[15px] font-semibold text-ink hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 pt-1 text-[14px] leading-relaxed text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>

        <FAQPageLd items={items} />
      </Container>
    </section>
  );
}

export const FAQConfig: ComponentConfig<FAQProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'text' },
    description: { type: 'textarea' },
    items: {
      type: 'array',
      arrayFields: { question: { type: 'text' }, answer: { type: 'textarea' } },
      defaultItemProps: { question: '', answer: '' },
    },
  },
  defaultProps: {
    eyebrow: 'Vos questions',
    title: 'Questions fréquentes',
    description:
      'Une question qui ne figure pas ici ? Écrivez-nous, on vous répond sous 24 h ouvrées.',
    items: [
      {
        question: 'Sur le courtage énergie, êtes-vous indépendants des fournisseurs ?',
        answer:
          'Oui. Sur le pôle courtage gaz / électricité, on est indépendant de chaque fournisseur (TotalEnergies, Engie, EDF Pro, Eni, Alpiq…). Aucun partenariat exclusif. On compare toutes les offres accessibles à votre profil.',
      },
      {
        question: 'Sur les primes énergie, comment fonctionnent vos partenariats ?',
        answer:
          "En tant qu'intermédiaire agréé, on travaille avec plusieurs fournisseurs d'énergie (TotalEnergies, EDF, Engie et d'autres). Ces partenariats sont transparents, détaillés dans nos conditions générales.",
      },
    ],
  },
  render: FAQ,
};
